import { FC } from 'react';
import { MAlert, MBox, MText } from '.';

interface ResolvedTermsPreviewProps {
  /** Heading above the rendered preview */
  label: string;
  /** Resolved term HTML; already escaped at interpolation time */
  html: string;
  isLoading: boolean;
  /** Renders the error banner when set; omit to suppress it for this instance */
  errorMessage?: string;
  /** Stable QA hook — must stay unique across a single screen */
  testId: string;
  /** Caps the preview height and scrolls, for constrained surfaces */
  maxH?: string;
  textColor?: string;
  mt?: number;
}

/**
 * Rendered preview of terms with their template expressions resolved.
 *
 * The HTML is injected as-is; values interpolated into it are escaped by
 * `replaceTemplateExpressionsWithValue`, so only the author's own term markup
 * reaches the DOM as markup.
 */
export const ResolvedTermsPreview: FC<ResolvedTermsPreviewProps> = ({
  label,
  html,
  isLoading,
  errorMessage,
  testId,
  maxH,
  textColor,
  mt = 3,
}) => (
  <MBox mt={mt}>
    <MText fontWeight="600" mb={1}>
      {label}
    </MText>
    {errorMessage ? (
      <MAlert type="error" data-testid={testId}>
        <MText>{errorMessage}</MText>
      </MAlert>
    ) : (
      <MBox
        className="rich-text"
        color={textColor}
        fontSize="sm"
        borderWidth="1px"
        borderColor="tGray.lightGray"
        borderRadius="md"
        p={3}
        maxH={maxH}
        overflowY={maxH ? 'auto' : undefined}
        opacity={isLoading ? 0.6 : 1}
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    )}
  </MBox>
);
